/**
 * The offer: agree to the season or don't. Everything a person needs to
 * decide is on this one page — what it costs, how many boxes, and an
 * honest paragraph about what they're actually signing up for — because
 * asking someone to click through several screens before they can say yes
 * is asking them to trust a farm that hasn't earned the click yet.
 */

import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';

import { apiGet, apiPost, ApiError } from '../api.js';
import { signOut } from '../auth.js';

interface Invite {
  name: string;
  season_year: number;
  delivery_count: number | null;
  box_price_minor: number | null;
  total_cost_minor: number | null;
  enrollment_required: boolean;
  shop_url: string;
}

function money(minor: number | null): string {
  if (minor == null) return 'whatever the box ends up costing';
  return `$${(minor / 100).toFixed(2)}`;
}

export function InviteView(): JSX.Element {
  const [invite, setInvite] = useState<Invite | 'not_invited' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    apiGet<Invite>('/member/v1/invite')
      .then(setInvite)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 400) {
          setInvite('not_invited');
        } else {
          setError(err instanceof ApiError ? err.message : 'Could not load your invite.');
        }
      });
  }, []);

  async function decline(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await apiPost('/member/v1/invite/decline', { reason });
      await signOut('/box?declined=1');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that. Try again in a moment.');
      setBusy(false);
    }
  }

  if (error) {
    return (
      <main class="view centred">
        <p class="error" role="alert">{error}</p>
      </main>
    );
  }

  if (invite === null) {
    return (
      <main class="view centred">
        <p class="muted" role="status">Loading…</p>
      </main>
    );
  }

  if (invite === 'not_invited') {
    return (
      <main class="view centred">
        <h1>Nothing waiting here</h1>
        <p class="muted">There's no open offer on this account right now.</p>
      </main>
    );
  }

  if (declining) {
    return (
      <main class="view auth-view">
        <h1>Before you go</h1>
        <p>
          Declining gives up your spot on the waitlist — the farm will offer it to the next person in
          line, and this account will be deactivated.
        </p>
        <label>
          Anything you'd want us to know? <span class="small muted">(optional)</span>
          <textarea value={reason} onInput={(event) => setReason((event.target as HTMLTextAreaElement).value)} />
        </label>
        {error && <p class="error" role="alert">{error}</p>}
        <div style="display: flex; gap: 0.75rem; flex-wrap: wrap;">
          <button class="primary" disabled={busy} onClick={() => void decline()}>
            {busy ? 'Working…' : 'I understand — decline'}
          </button>
          <button class="link" onClick={() => setDeclining(false)}>
            Never mind, take me back
          </button>
        </div>
      </main>
    );
  }

  return (
    <main class="view auth-view">
      <header>
        <h1>You're invited, {invite.name.trim().split(/\s+/)[0] || invite.name}</h1>
      </header>

      <p>
        Good news: there's a spot for you in the {invite.season_year} CSA. Bad news: this is a
        small, first-years vegetable farm, so "good news" is doing some heavy lifting in that
        sentence. What we can honestly promise is real vegetables, most weeks, grown on purpose by
        people who are still learning a lot of this as they go. What we can't promise is that the box
        will always be exactly what we said it would be — some week it may lean harder on lettuce
        than anyone would like, because that is just how lettuce works.
      </p>

      <div class="card">
        <span class="card-title">What you're agreeing to</span>
        <span class="card-sub">
          {invite.delivery_count != null ? `${invite.delivery_count} boxes` : 'A season of boxes'} over the{' '}
          {invite.season_year} season
        </span>
        <span class="card-sub">{money(invite.box_price_minor)} per box</span>
        {invite.total_cost_minor != null && <span class="card-sub">{money(invite.total_cost_minor)} for the season</span>}
        <span class="card-sub">
          To sign up, use the code in your invitation email. If you can't find it, reply to the email and
          we'll send a fresh one.
        </span>
      </div>

      {error && <p class="error" role="alert">{error}</p>}

      <div style="display: flex; gap: 0.75rem; flex-wrap: wrap;">
        <a class="primary" href={invite.shop_url}>
          Sign up with my code
        </a>
        <button class="link" onClick={() => setDeclining(true)}>
          I'll pass this season
        </button>
      </div>
    </main>
  );
}
